import { useEffect, useRef, useState } from "react";
import { CiSearch } from "react-icons/ci";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance";
import "./EquipmentGroup.css";
import { Typography } from "@mui/material";
import Loader from "../Components/Loader";
import PaginationFooter from "../Components/PaginationFooter.js";

function EquipmentGroup({ isSidebarExpanded }) {
  const [equipmentGroups, setEquipmentGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isSearching, setIsSearching] = useState(false);
  const hasInitializedItemsPerPage = useRef(false);
  const hasInitializedSearch = useRef(false);

  const fetchEquipmentGroups = async (page = 1, perPage = itemsPerPage, search = "") => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        ...(search && { search }), // Only add search if it exists
      };

      const response = await axiosInstance.get(
        `${API_URL}/api/get_all_equipment_groups`,
        { params }
      );
      console.log("Equipment Groups Response:", response.data);
      setEquipmentGroups(response.data?.data || []);

      // Extract pagination info
      const paginationData = response.data?.pagination || {};
      setTotalRecords(paginationData.total || 0);
    } catch (error) {
      console.error(
        "Failed to fetch equipment groups:",
        error.response?.data || error.message
      );
      setEquipmentGroups([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchEquipmentGroups(1, itemsPerPage, "");
  }, []);

  // Fetch when page changes
  useEffect(() => {
    if (currentPage > 1) {
      fetchEquipmentGroups(currentPage, itemsPerPage, searchTerm);
    }
  }, [currentPage]);

  // Fetch when items per page changes
  useEffect(() => {
    if (!hasInitializedItemsPerPage.current) {
      hasInitializedItemsPerPage.current = true;
      return;
    }
    setCurrentPage(1); // Reset to first page
    fetchEquipmentGroups(1, itemsPerPage, searchTerm);
  }, [itemsPerPage]);

  // Handle search with debounce (optional but recommended)
  useEffect(() => {
    if (!hasInitializedSearch.current) {
      hasInitializedSearch.current = true;
      return;
    }
    const delayTimer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      setIsSearching(searchTerm.trim().length > 0);
      fetchEquipmentGroups(1, itemsPerPage, searchTerm);
    }, 500); // 500ms debounce

    return () => clearTimeout(delayTimer);
  }, [searchTerm]);

  return (
    <div>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backdropFilter: "blur(6px)",
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              transform: "scale(2)",
              gap: "10px",
            }}
          >
            <Loader />
          </div>
        </div>
      )}
      <div
        className={`table-container eqgroup-page ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="eqgroup-page-content">
          <div className="eqgroup-page-header">
            <div className="eqgroup-header-titleblock">
              <h2 className="page-title mb-0">Equipment Group</h2>
            </div>
            <div className="eqgroup-header-buttons">
              <div className="eqgroup-header-controls-row">
                <div className="eqgroup-search-inline">
                  <CiSearch size={20} className="eqgroup-search-icon" />
                  <input
                    type="text"
                    value={searchTerm}
                    placeholder="Search Equipment Group..."
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <span
                      className="eqgroup-search-clear"
                      onClick={() => setSearchTerm("")}
                    >
                      &times;
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="eqgroup-table-card">
            <div className="eqgroup-table-wrapper">
              <table className="eqgroup-data-table">
                <colgroup>
                  <col className="eqgroup-col-sr" />
                  <col className="eqgroup-col-code" />
                  <col className="eqgroup-col-name" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Sr. No</th>
                    <th>Equipment Group Code</th>
                    <th>Equipment Group Name</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 10 }).map((_, rowIdx) => (
                      <tr key={`eqgroup-skeleton-${rowIdx}`}>
                        {Array.from({ length: 3 }).map((__, colIdx) => (
                          <td key={colIdx}>
                            <div
                              style={{
                                height: "10px",
                                background: "#e5e8ee",
                                borderRadius: "3px",
                                animation: "pulse 2s ease-in-out infinite",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : equipmentGroups.length > 0 ? (
                    equipmentGroups.map((item, index) => (
                      <tr key={item.id || index}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td>
                          <span className="eqgroup-code">
                            {item.equipment_group_code || "N/A"}
                          </span>
                        </td>
                        <td>{item.equipment_group_name || "N/A"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} style={{ textAlign: "center", padding: "24px" }}>
                        <Typography variant="body2">
                          No equipment groups found
                        </Typography>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationFooter
              totalRecords={totalRecords}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setItemsPerPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EquipmentGroup;
